import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import AuditLogPage from "./pages/AuditLogPage";
import AccessReviewPage from "./pages/AccessReviewPage";
import SecurityMonitorPage from "./pages/SecurityMonitorPage";
import IncidentsPage from "./pages/IncidentsPage";
import ChangeLogPage from "./pages/ChangeLogPage";
import NotFoundPage from "./pages/NotFoundPage";
import { CookieConsent } from "./components/CookieConsent";
import DashboardPage from "./pages/DashboardPage";
import ContractsPage from "./pages/ContractsPage";
import ContractDetailPage from "./pages/ContractDetailPage";
import ContractEditorPage from "./pages/ContractEditorPage";
import TemplatesPage from "./pages/TemplatesPage";
import ClausesPage from "./pages/ClausesPage";
import WorkflowsPage from "./pages/WorkflowsPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import ContractManagementPage from "./pages/ContractManagementPage";
import ApprovalPoliciesPage from "./pages/ApprovalPoliciesPage";
import CompliancePage from "./pages/CompliancePage";
import SupplierSigningPage from "./pages/SupplierSigningPage";
import SupplierReviewPage from "./pages/SupplierReviewPage";
import GuestReviewPage from "./pages/GuestReviewPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import HelpPage from "./pages/HelpPage";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import ContractCreationWizard from "./pages/ContractCreationWizard";
import OnboardingTour from "./components/OnboardingTour";

// Layout
import AppLayout from "./components/layout/AppLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// API instance with auth — uses httpOnly cookies (withCredentials)
export const apiClient = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Response interceptor — redirect on 401 (but not for /auth/me session check)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes("/auth/me")) {
      localStorage.removeItem("contrack_user");
      const idle = String(error.response?.data?.detail || "").includes("inactivity");
      window.location.href = idle ? "/login?reason=idle" : "/login";
    }
    return Promise.reject(error);
  }
);

// Sanitize user data for localStorage — only store non-sensitive UI state
const _safeUserCache = (userData) => {
  if (!userData) return null;
  const { id, name, email, role, org_id } = userData;
  return { id, name, email, role, org_id };
};


// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgFeatures, setOrgFeatures] = useState([]);
  const [showTour, setShowTour] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  const fetchOrgFeatures = async () => {
    try {
      const res = await apiClient.get("/organization");
      const plan = res.data?.plan;
      setOrgFeatures(plan?.all_enabled_features || []);
    } catch (e) { /* Org features load silently — non-critical for page render */ }
  };

  useEffect(() => {
    // Check if we have a valid session (cookie-based)
    const storedUser = localStorage.getItem("contrack_user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch { /* corrupt data */ }
    }
    // Validate session by calling a lightweight endpoint
    apiClient.get("/auth/me").then((res) => {
      const userData = res.data;
      localStorage.setItem("contrack_user", JSON.stringify(_safeUserCache(userData)));
      setUser(userData);
      fetchOrgFeatures();
    }).catch(() => {
      // No valid session cookie
      localStorage.removeItem("contrack_user");
      setUser(null);
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      const data = response.data;

      // MFA required — return challenge info
      if (data.mfa_required) {
        return {
          success: false,
          mfa_required: true,
          mfa_token: data.mfa_token,
          mfa_setup_complete: data.mfa_setup_complete,
          mfa_enforced: data.mfa_enforced,
        };
      }

      const { user: userData } = data;
      
      // Cookie is set automatically by the backend response (httpOnly)
      localStorage.setItem("contrack_user", JSON.stringify(_safeUserCache(userData)));
      setUser(userData);
      fetchOrgFeatures();

      // Check if first login — trigger onboarding tour
      const tourKey = `contrack_tour_done_${userData.id}`;
      if (!localStorage.getItem(tourKey)) {
        setTimeout(() => setShowWelcome(true), 1500);
      }

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || "Login failed" 
      };
    }
  };

  const verifyMfa = async (mfaToken, totpCode) => {
    try {
      const response = await axios.post(`${API}/auth/mfa/verify`, { mfa_token: mfaToken, totp_code: totpCode }, { withCredentials: true });
      const { user: userData } = response.data;

      localStorage.setItem("contrack_user", JSON.stringify(_safeUserCache(userData)));
      setUser(userData);
      fetchOrgFeatures();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || "Verification failed",
      };
    }
  };

  const getMfaSetup = async (mfaToken) => {
    try {
      const response = await axios.post(`${API}/auth/mfa/setup`, { mfa_token: mfaToken, totp_code: "" }, { withCredentials: true });
      return { success: true, qr_uri: response.data.qr_uri, secret: response.data.secret };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || "Setup failed" };
    }
  };

  const register = async (name, email, password, role = "contract_creator", department = "") => {
    try {
      const response = await axios.post(`${API}/auth/register`, { 
        name, 
        email, 
        password,
        role,
        department 
      }, { withCredentials: true });
      const { user: userData } = response.data;
      
      localStorage.setItem("contrack_user", JSON.stringify(_safeUserCache(userData)));
      setUser(userData);
      fetchOrgFeatures();
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || "Registration failed" 
      };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (e) { /* Logout API call is best-effort — cookie cleared regardless */ }
    localStorage.removeItem("contrack_user");
    setUser(null);
  };

  // Idle sign-out after 30 minutes; activity is shared across tabs via localStorage
  useEffect(() => {
    if (!user) return;
    const KEY = "contrack_last_activity";
    const TIMEOUT_MS = 30 * 60 * 1000;
    const WARN_MS = 28 * 60 * 1000;
    const HEARTBEAT_MS = 5 * 60 * 1000;
    let lastBeat = Date.now();
    let warned = false;
    const lastActivity = () => Number(localStorage.getItem(KEY)) || Date.now();
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivity() < 5000) return;
      localStorage.setItem(KEY, String(now));
      warned = false;
      if (now - lastBeat > HEARTBEAT_MS) {
        lastBeat = now;
        apiClient.post("/auth/heartbeat").catch(() => {});
      }
    };
    localStorage.setItem(KEY, String(Date.now()));
    const check = setInterval(async () => {
      const idle = Date.now() - lastActivity();
      if (idle >= TIMEOUT_MS) {
        clearInterval(check);
        await logout();
        window.location.href = "/login?reason=idle";
      } else if (idle >= WARN_MS && !warned) {
        warned = true;
        toast.warning("You'll be signed out in 2 minutes due to inactivity. Move the mouse or press a key to stay signed in.");
      }
    }, 30000);
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      clearInterval(check);
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [user]);

  const hasFeature = (feature) => {
    if (user?.role === "superuser" || user?.role === "admin") return true;
    return orgFeatures.includes(feature);
  };

  const canDo = (action, contractCreatedBy = "", contractOwnerId = "") => {
    if (!user) return false;
    const perms = user.action_permissions;
    if (!perms) return user.role === "superuser" || user.role === "admin";
    const perm = perms[action];
    if (perm === "all") return true;
    if (perm === "no" || !perm) return false;
    if (perm === "own" || perm === "assigned") {
      return user.id === contractCreatedBy || user.id === contractOwnerId;
    }
    if (perm === "view") return action.endsWith(".view");
    return false;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    hasFeature,
    canDo,
    verifyMfa,
    getMfaSetup,
    showTour,
    setShowTour,
    showWelcome,
    setShowWelcome,
    startTour: () => { setShowWelcome(false); setShowTour(true); },
    completeTour: () => {
      setShowTour(false);
      if (user) localStorage.setItem(`contrack_tour_done_${user.id}`, "true");
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Main App Component
// Tour Manager — Welcome modal + OnboardingTour
const TourManager = () => {
  const { user, showTour, showWelcome, setShowWelcome, startTour, completeTour } = useAuth();

  return (
    <>
      {/* Welcome modal */}
      {showWelcome && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50" data-testid="welcome-modal">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl max-w-sm w-full mx-4 overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-cyan-100 dark:bg-cyan-900 flex items-center justify-center mx-auto mb-4">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-cyan-600">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Welcome to ConTrack</h2>
              <p className="text-sm text-slate-500 mb-6">
                Take a quick guided tour to discover the key features of your contract management platform.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={startTour}
                  className="w-full py-2.5 px-4 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-md transition-colors"
                  data-testid="start-tour-btn"
                >
                  Take the tour (2 min)
                </button>
                <button
                  onClick={() => { setShowWelcome(false); completeTour(); }}
                  className="w-full py-2 px-4 text-sm text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="skip-tour-btn"
                >
                  Skip for now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Tour */}
      {showTour && (
        <OnboardingTour
          userRole={user?.role || "external"}
          onComplete={completeTour}
        />
      )}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: "'Public Sans', sans-serif",
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/sign/:token" element={<SupplierSigningPage />} />
          <Route path="/review/:token" element={<GuestReviewPage />} />
          <Route path="/invite/:token" element={<AcceptInvitePage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/contracts" element={<ContractsPage />} />
                    <Route path="/contracts/new" element={<ContractEditorPage />} />
                    <Route path="/contracts/new/wizard" element={<ContractCreationWizard />} />
                    <Route path="/contracts/:id" element={<ContractDetailPage />} />
                    <Route path="/contracts/:id/edit" element={<ContractEditorPage />} />
                    <Route path="/contract-management" element={<ContractManagementPage />} />
                    <Route path="/templates" element={<TemplatesPage />} />
                    <Route path="/clauses" element={<ClausesPage />} />
                    <Route path="/workflows" element={<WorkflowsPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/users" element={<UsersPage />} />
                    <Route path="/approval-policies" element={<ApprovalPoliciesPage />} />
                    <Route path="/compliance" element={<CompliancePage />} />
                    <Route path="/supplier-review" element={<SupplierReviewPage />} />
                    <Route path="/audit-log" element={<AuditLogPage />} />
                    <Route path="/access-review" element={<AccessReviewPage />} />
                    <Route path="/security-monitor" element={<SecurityMonitorPage />} />
                    <Route path="/incidents" element={<IncidentsPage />} />
                    <Route path="/change-log" element={<ChangeLogPage />} />
                    <Route path="/platform" element={<PlatformAdminPage />} />
                    <Route path="/help" element={<HelpPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </AppLayout>
                <TourManager />
              </ProtectedRoute>
            }
          />
        </Routes>
        <CookieConsent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
